import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchChatSuggestions } from 'services/travProAgentStream';
import type { Message } from './types';
import {
  QUICK_SUGGESTION_COUNT,
  canRotateSuggestionPairs,
  getFallbackSuggestions,
  randomizeSuggestionList,
  sliceSuggestionPair,
} from './smartAssistUtils';

export interface UseSuggestionsProps {
  appId?: number | string | null;
  baseUrl: string;
  getToken: () => Promise<string | null>;
  messages: Message[];
}

export interface UseSuggestionsReturn {
  quickSuggestionsHome: string[];
  quickSuggestionsChat: string[];
  showHomeSuggestionSwitch: boolean;
  showChatSuggestionSwitch: boolean;
  rotateHomeSuggestions: () => void;
  rotateChatSuggestions: () => void;
}

export function useSuggestions({
  appId,
  baseUrl,
  getToken,
  messages,
}: UseSuggestionsProps): UseSuggestionsReturn {
  const [suggestions, setSuggestions] = useState<string[]>(() => randomizeSuggestionList(getFallbackSuggestions()));
  const [homePairIndex, setHomePairIndex] = useState(0);
  const [chatPairIndex, setChatPairIndex] = useState(0);

  useEffect(() => {
    const request = { active: true };
    void (async () => {
      const n = Number(appId);
      const aid = n > 0 ? n : Number(import.meta.env.VITE_APP_ID);
      if (!aid || Number.isNaN(aid)) return;
      const token = await getToken();
      if (!token || !request.active) return;
      try {
        const list = await fetchChatSuggestions({ baseUrl, token, appId: String(aid) });
        if (request.active && list.length > 0) setSuggestions(randomizeSuggestionList(list));
      } catch {
        /* keep translated fallback suggestions */
      }
    })();
    return () => { request.active = false; };
  }, [appId, baseUrl, getToken]);

  useEffect(() => { setHomePairIndex(0); }, [suggestions]);

  // Serialise user texts to a stable key so pool only recomputes on actual changes
  const usedTextsKey = JSON.stringify(messages.filter((m) => m.role === 'user').map((m) => m.text));

  const chatSuggestionPool = useMemo(() => {
    const used = new Set<string>(JSON.parse(usedTextsKey) as string[]);
    return suggestions.filter((s) => !used.has(s));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions, usedTextsKey]);

  useEffect(() => { setChatPairIndex(0); }, [suggestions, usedTextsKey]);

  const quickSuggestionsHome = useMemo(
    () => sliceSuggestionPair(suggestions, homePairIndex, QUICK_SUGGESTION_COUNT),
    [suggestions, homePairIndex],
  );

  const quickSuggestionsChat = useMemo(
    () => sliceSuggestionPair(chatSuggestionPool, chatPairIndex, QUICK_SUGGESTION_COUNT),
    [chatSuggestionPool, chatPairIndex],
  );

  const rotateHomeSuggestions = useCallback((): void => {
    setHomePairIndex((i) => (i + 1) % Math.max(1, Math.ceil(suggestions.length / QUICK_SUGGESTION_COUNT)));
  }, [suggestions.length]);

  const rotateChatSuggestions = useCallback((): void => {
    setChatPairIndex((i) => (i + 1) % Math.max(1, Math.ceil(chatSuggestionPool.length / QUICK_SUGGESTION_COUNT)));
  }, [chatSuggestionPool.length]);

  return {
    quickSuggestionsHome,
    quickSuggestionsChat,
    showHomeSuggestionSwitch: canRotateSuggestionPairs(suggestions, QUICK_SUGGESTION_COUNT),
    showChatSuggestionSwitch: canRotateSuggestionPairs(chatSuggestionPool, QUICK_SUGGESTION_COUNT),
    rotateHomeSuggestions,
    rotateChatSuggestions,
  };
}
